// Контракт согласован в Outline: https://wiki.telegraphyx.ru/doc/tz-otchyot-po-reklamnym-kampaniyam-dlya-lk-fJdtgTyz5j

export interface MetricRow {
  cost: number
  impressions: number
  ctr: number
  clicks: number
  cpc: number
  landingConv: number
  visits: number
  cpaVisitor: number
  pdpConv: number
  pdp: number
  cpaPdp: number
  unsubscribed: number
  churnRate: number
  netPdp: number
  netPdpCost: number
}

export interface DailyRow extends MetricRow {
  date: string // 'YYYY-MM-DD' для строк, 'total' в totals
}

export interface DailyReport {
  rows: DailyRow[]
  totals: DailyRow
}

export interface CampaignAd {
  adID: string
  adTitle: string
  metrics: MetricRow
}

export interface CampaignRow {
  campaignID: string
  campaignName: string
  metrics: MetricRow
  ads: CampaignAd[]
}

export interface ByCampaignReport {
  rows: CampaignRow[]
  totals: MetricRow
  hasMore: boolean
  totalCampaigns: number
}

export interface CampaignsListEntry {
  campaignID: string
  campaignName: string
  adsCount: number
  totalSpend: number
  ads: Array<{ adID: string; adTitle: string }>
}

export interface CampaignsList {
  campaigns: CampaignsListEntry[]
}

export interface ReportParams {
  channelUUID: string
  from: string // YYYY-MM-DD
  to: string
  campaignIds?: string[]
}

// ────────────────── REAL API ──────────────────

const USE_MOCKS = false // PR1 в проде, контракт совпадает

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) {
    let body: { error?: string } = {}
    try { body = await res.json() } catch {}
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function buildQuery(p: ReportParams, extra: Record<string, string | number> = {}): string {
  const params = new URLSearchParams({
    channelUUID: p.channelUUID,
    from: p.from,
    to: p.to,
  })
  if (p.campaignIds && p.campaignIds.length > 0) {
    params.set('campaignIds', p.campaignIds.join(','))
  }
  for (const [k, v] of Object.entries(extra)) {
    params.set(k, String(v))
  }
  return params.toString()
}

export async function getCampaignsList(p: ReportParams): Promise<CampaignsList> {
  if (USE_MOCKS) return mockCampaignsList(p)
  return get<CampaignsList>(`/api/max/ads-report/campaigns-list?${buildQuery(p)}`)
}

export async function getDailyReport(p: ReportParams): Promise<DailyReport> {
  if (USE_MOCKS) return mockDailyReport(p)
  return get<DailyReport>(`/api/max/ads-report/daily?${buildQuery(p)}`)
}

export async function getByCampaignReport(
  p: ReportParams,
  opts: { limit?: number; offset?: number } = {},
): Promise<ByCampaignReport> {
  if (USE_MOCKS) return mockByCampaign(p)
  return get<ByCampaignReport>(
    `/api/max/ads-report/by-campaign?${buildQuery(p, { limit: opts.limit ?? 10, offset: opts.offset ?? 0 })}`,
  )
}

// ────────────────── METRIC LABELS ──────────────────

export type MetricKey = keyof MetricRow

export const METRIC_LABELS: Record<MetricKey, string> = {
  cost: 'Расход, ₽',
  impressions: 'Показы',
  ctr: 'CTR %',
  clicks: 'Клики',
  cpc: 'CPC',
  landingConv: 'C% лендинга',
  visits: 'Переходов в канал',
  cpaVisitor: 'CPA посетителя',
  pdpConv: 'C% в ПДП',
  pdp: 'ПДП',
  cpaPdp: 'Стоимость ПДП, ₽',
  unsubscribed: 'Отписки',
  churnRate: 'Отписки, %',
  netPdp: 'Итого ПДП',
  netPdpCost: 'Итого ПДП, ₽',
}

// Какие метрики — деньги (2 знака после запятой), какие — % (2 знака с %), какие — int
export const METRIC_FORMAT: Record<MetricKey, 'rub' | 'pct' | 'int' | 'num2'> = {
  cost: 'rub',
  impressions: 'int',
  ctr: 'pct',
  clicks: 'int',
  cpc: 'num2',
  landingConv: 'pct',
  visits: 'int',
  cpaVisitor: 'num2',
  pdpConv: 'pct',
  pdp: 'int',
  cpaPdp: 'rub',
  unsubscribed: 'int',
  churnRate: 'pct',
  netPdp: 'int',
  netPdpCost: 'rub',
}

export const METRIC_KEYS: MetricKey[] = Object.keys(METRIC_LABELS) as MetricKey[]

// ────────────────── MOCKS ──────────────────

function rng(seed: number): () => number {
  let s = seed
  return () => {
    s = (s * 9301 + 49297) % 233280
    return s / 233280
  }
}

function daysBetween(from: string, to: string): string[] {
  const result: string[] = []
  const start = new Date(from + 'T00:00:00')
  const end = new Date(to + 'T00:00:00')
  for (let d = new Date(start); d.getTime() <= end.getTime(); d.setDate(d.getDate() + 1)) {
    result.push(d.toISOString().slice(0, 10))
  }
  return result
}

function makeMetrics(r: () => number, base = 1): MetricRow {
  const impressions = Math.floor((3000 + r() * 15000) * base)
  const clicks = Math.floor(impressions * (0.01 + r() * 0.04))
  const cost = Math.round((clicks * (40 + r() * 80)) * 100) / 100
  const visits = Math.floor(clicks * (0.6 + r() * 0.3))
  const subscribed = Math.floor(visits * (0.1 + r() * 0.25))
  const unsubscribed = Math.floor(subscribed * (0.05 + r() * 0.2))
  const netPdp = subscribed - unsubscribed
  return {
    cost,
    impressions,
    ctr: impressions > 0 ? Math.round((clicks / impressions) * 10000) / 100 : 0,
    clicks,
    cpc: clicks > 0 ? Math.round((cost / clicks) * 100) / 100 : 0,
    landingConv: clicks > 0 ? Math.round((visits / clicks) * 10000) / 100 : 0,
    visits,
    cpaVisitor: visits > 0 ? Math.round((cost / visits) * 100) / 100 : 0,
    pdpConv: visits > 0 ? Math.round((subscribed / visits) * 10000) / 100 : 0,
    pdp: subscribed,
    cpaPdp: subscribed > 0 ? Math.round((cost / subscribed) * 100) / 100 : 0,
    unsubscribed,
    churnRate: subscribed > 0 ? Math.round((unsubscribed / subscribed) * 10000) / 100 : 0,
    netPdp,
    netPdpCost: netPdp > 0 ? Math.round((cost / netPdp) * 100) / 100 : 0,
  }
}

function sumMetrics(rows: MetricRow[]): MetricRow {
  const totals: MetricRow = {
    cost: 0, impressions: 0, ctr: 0, clicks: 0, cpc: 0, landingConv: 0,
    visits: 0, cpaVisitor: 0, pdpConv: 0, pdp: 0, cpaPdp: 0,
    unsubscribed: 0, churnRate: 0, netPdp: 0, netPdpCost: 0,
  }
  for (const r of rows) {
    totals.cost += r.cost
    totals.impressions += r.impressions
    totals.clicks += r.clicks
    totals.visits += r.visits
    totals.pdp += r.pdp
    totals.unsubscribed += r.unsubscribed
    totals.netPdp += r.netPdp
  }
  totals.ctr = totals.impressions > 0 ? Math.round((totals.clicks / totals.impressions) * 10000) / 100 : 0
  totals.cpc = totals.clicks > 0 ? Math.round((totals.cost / totals.clicks) * 100) / 100 : 0
  totals.landingConv = totals.clicks > 0 ? Math.round((totals.visits / totals.clicks) * 10000) / 100 : 0
  totals.cpaVisitor = totals.visits > 0 ? Math.round((totals.cost / totals.visits) * 100) / 100 : 0
  totals.pdpConv = totals.visits > 0 ? Math.round((totals.pdp / totals.visits) * 10000) / 100 : 0
  totals.cpaPdp = totals.pdp > 0 ? Math.round((totals.cost / totals.pdp) * 100) / 100 : 0
  totals.churnRate = totals.pdp > 0 ? Math.round((totals.unsubscribed / totals.pdp) * 10000) / 100 : 0
  totals.netPdpCost = totals.netPdp > 0 ? Math.round((totals.cost / totals.netPdp) * 100) / 100 : 0
  totals.cost = Math.round(totals.cost * 100) / 100
  return totals
}

const MOCK_CAMPAIGNS: Array<{ campaignID: string; campaignName: string; ads: Array<{ adID: string; adTitle: string }> }> = [
  {
    campaignID: '125663214',
    campaignName: 'telegraphyx_poisk_offer2_20260506',
    ads: [
      { adID: '12566321401', adTitle: 'Увеличьте охват на 50%: секреты маркетинга для вас!' },
      { adID: '12566321402', adTitle: 'Кофе как у бариста — научим за вечер' },
    ],
  },
  {
    campaignID: '125663220',
    campaignName: 'ns_1205_poisk_contentai_rf_smm_08',
    ads: [
      { adID: '12566322001', adTitle: 'Автоматизация СММ: контент без рутины' },
      { adID: '12566322002', adTitle: 'AI пишет посты за вас — попробуйте бесплатно' },
    ],
  },
  {
    campaignID: '125663231',
    campaignName: 'telegraphyx_rsya_lookalike_20260510',
    ads: [
      { adID: '12566323101', adTitle: 'Канал для тех, кто любит хороший кофе' },
      { adID: '12566323102', adTitle: 'Подпишитесь — получайте обзоры зёрен' },
    ],
  },
  {
    campaignID: '125663247',
    campaignName: 'telegraphyx_poisk_brand_20260512',
    ads: [{ adID: '12566324701', adTitle: 'Telegraphyx — рабочая аналитика канала' }],
  },
  {
    campaignID: '125663260',
    campaignName: 'telegraphyx_rsya_retargeting_20260513',
    ads: [
      { adID: '12566326001', adTitle: 'Вернитесь — у нас новые гайды' },
      { adID: '12566326002', adTitle: 'Не пропустите свежие подборки' },
    ],
  },
]

function mockSeed(p: ReportParams): number {
  let s = 0
  for (const ch of p.channelUUID + p.from + p.to) s = (s * 31 + ch.charCodeAt(0)) >>> 0
  return s || 1
}

async function mockCampaignsList(p: ReportParams): Promise<CampaignsList> {
  await new Promise((r) => setTimeout(r, 200))
  const r = rng(mockSeed(p))
  const campaigns = MOCK_CAMPAIGNS.map((c) => ({
    ...c,
    adsCount: c.ads.length,
    totalSpend: Math.round((5000 + r() * 50000) * 100) / 100,
  }))
  campaigns.sort((a, b) => b.totalSpend - a.totalSpend)
  return { campaigns }
}

async function mockDailyReport(p: ReportParams): Promise<DailyReport> {
  await new Promise((r) => setTimeout(r, 300))
  const r = rng(mockSeed(p))
  const days = daysBetween(p.from, p.to)
  const campaignsCount = p.campaignIds && p.campaignIds.length > 0 ? p.campaignIds.length : MOCK_CAMPAIGNS.length
  const scale = campaignsCount / MOCK_CAMPAIGNS.length
  const rows: DailyRow[] = days.map((date) => ({ date, ...makeMetrics(r, scale) }))
  return { rows, totals: { date: 'total', ...sumMetrics(rows) } }
}

async function mockByCampaign(p: ReportParams): Promise<ByCampaignReport> {
  await new Promise((r) => setTimeout(r, 350))
  const r = rng(mockSeed(p) ^ 0xa5a5)
  const days = daysBetween(p.from, p.to).length
  const filterIds = p.campaignIds && p.campaignIds.length > 0 ? new Set(p.campaignIds) : null
  const filtered = filterIds ? MOCK_CAMPAIGNS.filter((c) => filterIds.has(c.campaignID)) : MOCK_CAMPAIGNS
  const rows: CampaignRow[] = filtered.map((c) => {
    const ads: CampaignAd[] = c.ads.map((ad) => ({
      adID: ad.adID,
      adTitle: ad.adTitle,
      metrics: makeMetrics(r, days * 0.5),
    }))
    const metrics = sumMetrics(ads.map((a) => a.metrics))
    return { campaignID: c.campaignID, campaignName: c.campaignName, metrics, ads }
  })
  rows.sort((a, b) => b.metrics.cost - a.metrics.cost)
  return {
    rows,
    totals: sumMetrics(rows.map((c) => c.metrics)),
    hasMore: false,
    totalCampaigns: rows.length,
  }
}
